import {
  FREQUENCY_ENUM_TO_NAME,
  FREQUENCY_NAME_TO_ENUM,
  FrequencyUnit,
  presetToPair
} from 'utils/frequency'

/**
 * Cadência de um plano já normalizada: o par canônico (unidade, quantidade) e a
 * janela de ciclos. É o que decide se as recorrências que usam o plano mudariam.
 */
export interface PlanCadence {
  unique: boolean
  frequency: number
  unit: FrequencyUnit
  count: number
  totalCycles: number
}

type CadenceSource = {
  frequency?: number | string | null
  frequency_type?: string | null
  frequency_number?: number | string | null
}

/**
 * A API devolve `frequency` como nome do enum ("monthly") e o formulário guarda o
 * inteiro (1). Comparar os dois crus dá `NaN !== 1`, e toda edição parecia mudar a
 * frequência (SC-11643).
 */
export const toFrequencyEnum = (
  frequency?: number | string | null
): number | null => {
  if (frequency === null || frequency === undefined || frequency === '')
    return null
  if (typeof frequency === 'string' && frequency in FREQUENCY_NAME_TO_ENUM)
    return FREQUENCY_NAME_TO_ENUM[frequency]
  const value = Number(frequency)
  return Number.isNaN(value) ? null : value
}

const toPair = (
  frequency: number,
  source: CadenceSource
): [FrequencyUnit, number] =>
  presetToPair(
    FREQUENCY_ENUM_TO_NAME[frequency] ?? 'monthly',
    (source.frequency_type ?? undefined) as FrequencyUnit | undefined,
    Number(source.frequency_number) || undefined
  )

/** Cadência atual do plano, como veio da API (já passada pelo constructorProducts). */
export const productCadence = (
  product: CadenceSource & { totalCycles?: number | null }
): PlanCadence => {
  const totalCycles = Number(product?.totalCycles ?? 0) || 0
  const frequency = toFrequencyEnum(product?.frequency)
  // Mesma regra do formulário ao abrir o plano: sem frequência ou 1 ciclo é cobrança única.
  const unique = frequency === null || totalCycles === 1
  const [unit, count] = toPair(frequency ?? 1, product ?? {})

  return { unique, frequency: frequency ?? 1, unit, count, totalCycles }
}

/**
 * Cadência que o formulário vai gravar. Ciclos acima de 12 ficam no form como "Outros"
 * (13) + `totalCyclesCustom`; aqui volta o número real, igual ao que vai no PATCH.
 */
export const formCadence = (
  dataForm: CadenceSource & {
    chargeType?: number | string
    totalCycles?: number | string | null
    totalCyclesCustom?: string | null
  }
): PlanCadence => {
  const unique = Number(dataForm?.chargeType) === 0
  const rawCycles = Number(dataForm?.totalCycles ?? 0) || 0
  const totalCycles = unique
    ? 1
    : rawCycles === 13
    ? Number(dataForm?.totalCyclesCustom) || 0
    : rawCycles
  const frequency = toFrequencyEnum(dataForm?.frequency) ?? 1
  const [unit, count] = toPair(frequency, dataForm ?? {})

  return { unique, frequency, unit, count, totalCycles }
}

/**
 * A cadência mudou? Só aí faz sentido perguntar se as recorrências existentes devem
 * acompanhar. Compara o par canônico (não o rótulo) e a janela de ciclos.
 */
export const hasCadenceChanged = (
  current: PlanCadence,
  next: PlanCadence
): boolean => {
  if (current.unique && next.unique) return false
  if (current.unique !== next.unique) return true

  return (
    current.unit !== next.unit ||
    current.count !== next.count ||
    current.totalCycles !== next.totalCycles
  )
}
